import React, { Fragment, useState, useEffect } from 'react';
import { nonConformitiesApi, usersApi, processesApi, useAuth } from '../api';
import ExportButtons from '../components/ExportButtons';
import ColumnSelector from '../components/ColumnSelector';
import useColumnVisibility from '../hooks/useColumnVisibility';
import SectorFilter, { useSectorFilter } from '../components/SectorFilter';
import { toast } from 'sonner';
import { validateDateFields, extractApiError } from '../utils/dateValidation';
import {
  RefreshCw,
  Plus,
  Search,
  Edit,
  Trash2,
  X,
  Loader2,
  AlertTriangle,
  CheckCircle,
  Clock,
  XCircle,
  Eye,
} from 'lucide-react';

const NC_TYPES = [
  { value: 'product', label: 'Produto' },
  { value: 'process', label: 'Processo' },
  { value: 'service', label: 'Serviço' },
  { value: 'system', label: 'Sistema' },
  { value: 'supplier', label: 'Fornecedor' },
  { value: 'customer', label: 'Cliente' },
  { value: 'audit', label: 'Auditoria' },
  { value: 'other', label: 'Outros' },
];

const NC_ORIGINS = [
  { value: 'internal_audit', label: 'Auditoria Interna' },
  { value: 'external_audit', label: 'Auditoria Externa' },
  { value: 'customer_complaint', label: 'Reclamação de Cliente' },
  { value: 'inspection', label: 'Inspeção' },
  { value: 'process_control', label: 'Controle de Processo' },
  { value: 'supplier', label: 'Fornecedor' },
  { value: 'employee', label: 'Colaborador' },
  { value: 'other', label: 'Outros' },
];

const SEVERITIES = [
  { value: 'minor', label: 'Menor', color: 'badge-blue' },
  { value: 'major', label: 'Maior', color: 'badge-yellow' },
  { value: 'critical', label: 'Crítica', color: 'badge-red' },
];

const STATUS_CONFIG = {
  open: { label: 'Aberta', color: 'badge-yellow', icon: AlertTriangle },
  analyzing: { label: 'Em Análise', color: 'badge-blue', icon: RefreshCw },
  action_pending: { label: 'Ação Pendente', color: 'badge-yellow', icon: Clock },
  in_progress: { label: 'Em Andamento', color: 'badge-blue', icon: RefreshCw },
  verification_pending: { label: 'Aguardando Verificação', color: 'badge-yellow', icon: Clock },
  closed: { label: 'Encerrada', color: 'badge-green', icon: CheckCircle },
  cancelled: { label: 'Cancelada', color: 'badge-gray', icon: XCircle },
};

const NonConformities = () => {
  const { user, loading: authLoading } = useAuth();
  const { sectorFilter, setSectorFilter, effectiveFilter } = useSectorFilter(user);
  
  const [ncs, setNcs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterSeverity, setFilterSeverity] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [editingNc, setEditingNc] = useState(null);
  const [selectedNc, setSelectedNc] = useState(null);
  const [users, setUsers] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    nc_type: 'process',
    origin: 'internal_audit',
    severity: 'minor',
    area: '',
    process: '',
    evidence: '',
    immediate_action: '',
    occurrence_date: '',
    target_date: '',
    responsible_id: '',
  });
  const [saving, setSaving] = useState(false);
  const [stats, setStats] = useState(null);
  const [processes, setProcesses] = useState([]);

  useEffect(() => {
    // Wait for AuthProvider to resolve the user from localStorage first — otherwise
    // effectiveFilter starts as '' and flips to the user's sector a tick later,
    // firing this effect a second time with the correct filter.
    if (authLoading) return;
    loadData();
    loadUsers();
    loadProcesses();
  }, [authLoading, filterStatus, filterSeverity, effectiveFilter]);

  const loadData = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterStatus) params.status = filterStatus;
      if (filterSeverity) params.severity = filterSeverity;
      if (effectiveFilter) params.area = effectiveFilter;
      
      const [ncsRes, statsRes] = await Promise.all([
        nonConformitiesApi.list(params),
        nonConformitiesApi.stats(),
      ]);
      setNcs(ncsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const loadProcesses = async () => {
    try {
      const response = await processesApi.listActive();
      setProcesses(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar processos:', error);
    }
  };

  const loadUsers = async () => {
    try {
      const res = await usersApi.list({ is_active: true });
      setUsers(res.data);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const handleOpenModal = async (nc = null) => {
    if (nc) {
      try {
        const res = await nonConformitiesApi.get(nc.id);
        const fullNc = res.data;
        setEditingNc(fullNc);
        setFormData({
          title: fullNc.title || '',
          description: fullNc.description || '',
          nc_type: fullNc.nc_type || 'process',
          origin: fullNc.origin || 'internal_audit',
          severity: fullNc.severity || 'minor',
          area: fullNc.area || '',
          process: fullNc.process || '',
          evidence: fullNc.evidence || '',
          immediate_action: fullNc.immediate_action || '',
          occurrence_date: fullNc.occurrence_date || '',
          target_date: fullNc.target_date || '',
          responsible_id: fullNc.responsible_id || '',
        });
        setShowModal(true);
      } catch (error) {
        toast.error('Erro ao carregar dados da NC');
      }
    } else {
      setEditingNc(null);
      setFormData({
        title: '',
        description: '',
        nc_type: 'process',
        origin: 'internal_audit',
        severity: 'minor',
        area: '',
        process: '',
        evidence: '',
        immediate_action: '',
        occurrence_date: '',
        target_date: '',
        responsible_id: '',
      });
      setShowModal(true);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingNc(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.description) {
      toast.error('Preencha os campos obrigatórios');
      return;
    }

    const dateError = validateDateFields([
      { value: formData.occurrence_date, label: 'Data da Ocorrência' },
      { value: formData.target_date, label: 'Data Alvo' },
    ]);
    if (dateError) {
      toast.error(dateError);
      return;
    }
    
    setSaving(true);
    
    try {
      const data = {
        ...formData,
        responsible_id: formData.responsible_id || null,
        occurrence_date: formData.occurrence_date || null,
        target_date: formData.target_date || null,
      };

      if (editingNc) {
        await nonConformitiesApi.update(editingNc.id, data);
        toast.success('NC atualizada!');
      } else {
        await nonConformitiesApi.create(data);
        toast.success('NC registrada!');
      }
      handleCloseModal();
      loadData();
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao salvar'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (nc) => {
    if (!window.confirm(`Excluir NC "${nc.code}"?`)) return;
    
    try {
      await nonConformitiesApi.delete(nc.id);
      toast.success('NC excluída!');
      loadData();
    } catch (error) {
      toast.error('Erro ao excluir');
    }
  };

  const handleViewDetails = async (nc) => {
    try {
      const res = await nonConformitiesApi.get(nc.id);
      setSelectedNc(res.data);
      setShowDetailModal(true);
    } catch (error) {
      toast.error('Erro ao carregar detalhes');
    }
  };

  const filteredNcs = ncs.filter(nc =>
    nc.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    nc.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    nc.area?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const StatusBadge = ({ status }) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.open;
    const Icon = config.icon;
    return (
      <span className={`badge ${config.color}`}>
        <Icon size={12} className="mr-1" />
        {config.label}
      </span>
    );
  };

  const SeverityBadge = ({ severity }) => {
    const config = SEVERITIES.find(s => s.value === severity) || SEVERITIES[0];
    return <span className={`badge ${config.color}`}>{config.label}</span>;
  };

  const COLUMNS = [
    { key: 'code', label: 'Código', locked: true, render: (nc) => <td className="font-mono text-green-400">{nc.code}</td> },
    { key: 'title', label: 'Título', render: (nc) => <td><div className="max-w-xs truncate">{nc.title}</div></td> },
    { key: 'type', label: 'Tipo', render: (nc) => <td>{NC_TYPES.find(t => t.value === nc.nc_type)?.label || nc.nc_type}</td> },
    { key: 'severity', label: 'Severidade', render: (nc) => <td><SeverityBadge severity={nc.severity} /></td> },
    { key: 'status', label: 'Status', render: (nc) => <td><StatusBadge status={nc.status} /></td> },
    { key: 'area', label: 'Área', render: (nc) => <td>{nc.area || '-'}</td> },
    { key: 'deadline', label: 'Prazo', render: (nc) => <td>{nc.target_date ? new Date(nc.target_date).toLocaleDateString('pt-BR') : '-'}</td> },
    { key: 'actions', label: 'Ações', locked: true, headerClass: 'text-right', render: (nc) => (
      <td className="text-right">
        <div className="flex justify-end gap-2">
          <button
            onClick={() => handleViewDetails(nc)}
            className="p-1 hover:text-blue-400 transition-colors"
            title="Ver detalhes"
          >
            <Eye size={16} />
          </button>
          <button
            onClick={() => handleOpenModal(nc)}
            className="p-1 hover:text-green-400 transition-colors"
            title="Editar"
          >
            <Edit size={16} />
          </button>
          <button
            onClick={() => handleDelete(nc)}
            className="p-1 hover:text-red-400 transition-colors"
            title="Excluir"
          >
            <Trash2 size={16} />
          </button>
        </div>
      </td>
    ) },
  ];

  const { visibleColumns, visibleKeys, toggleColumn, resetColumns } = useColumnVisibility({ storageKey: 'non-conformities', columns: COLUMNS });

  return (
    <div className="space-y-6" data-testid="non-conformities-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Gestão de Não Conformidades</h1>
          <p className="text-gray-400 text-sm">Registre e acompanhe não conformidades</p>
        </div>
        <div className="flex items-center gap-2">
          <ExportButtons module="non-conformities" filters={{ status: filterStatus, severity: filterSeverity }} />
          <ColumnSelector columns={COLUMNS} visibleKeys={visibleKeys} onToggle={toggleColumn} onReset={resetColumns} />
          <button
            onClick={() => handleOpenModal()}
            className="btn-primary"
            data-testid="new-nc-btn"
          >
            <Plus size={20} />
            Nova NC
          </button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="card p-4">
            <p className="text-gray-400 text-sm">Total</p>
            <p className="text-2xl font-bold text-white">{stats.total}</p>
          </div>
          <div className="card p-4">
            <p className="text-gray-400 text-sm">Abertas</p>
            <p className="text-2xl font-bold text-yellow-500">{stats.open}</p>
          </div>
          <div className="card p-4">
            <p className="text-gray-400 text-sm">Encerradas</p>
            <p className="text-2xl font-bold text-green-500">{stats.closed}</p>
          </div>
          <div className="card p-4">
            <p className="text-gray-400 text-sm">Críticas</p>
            <p className="text-2xl font-bold text-red-500">{stats.by_severity?.critical || 0}</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="card p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Buscar por código, título ou área..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input pl-10 w-full"
              data-testid="search-input"
            />
          </div>
          <SectorFilter
            user={user}
            value={sectorFilter}
            onChange={(val) => { setSectorFilter(val); }}
          />
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="input px-3 w-full sm:w-60"
            data-testid="status-filter"
          >
            <option value="">Todos os Status</option>
            {Object.entries(STATUS_CONFIG).map(([value, config]) => (
              <option key={value} value={value}>{config.label}</option>
            ))}
          </select>
          <select
            value={filterSeverity}
            onChange={(e) => setFilterSeverity(e.target.value)}
            className="input px-3 w-full sm:w-60"
            data-testid="severity-filter"
          >
            <option value="">Todas Severidades</option>
            {SEVERITIES.map(s => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
          <button onClick={loadData} className="btn-secondary" data-testid="refresh-btn">
            <RefreshCw size={20} />
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="animate-spin text-green-500" size={32} />
          </div>
        ) : filteredNcs.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            Nenhuma não conformidade encontrada
          </div>
        ) : (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  {visibleColumns.map(col => (
                    <th key={col.key} className={col.headerClass || ''}>{col.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredNcs.map((nc) => (
                  <tr key={nc.id} data-testid={`nc-row-${nc.id}`}>
                    {visibleColumns.map(col => (
                      <Fragment key={col.key}>{col.render(nc)}</Fragment>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <>
          <div className="modal-backdrop" onClick={handleCloseModal} />
          <div className="modal-content p-6 max-w-2xl">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white">
                {editingNc ? 'Editar Não Conformidade' : 'Nova Não Conformidade'}
              </h2>
              <button onClick={handleCloseModal} className="text-gray-400 hover:text-white">
                <X size={24} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Título *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  className="input w-full"
                  placeholder="Título da não conformidade"
                  maxLength={150}
                  data-testid="nc-title-input"
                />
                <div className={`text-right text-xs mt-1 ${(formData.title || '').length >= 150 ? 'text-red-400' : 'text-gray-500'}`}>
                  {(formData.title || '').length}/150
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Descrição *</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="input w-full"
                  rows={3}
                  placeholder="Descreva a não conformidade encontrada"
                  maxLength={2000}
                  data-testid="nc-description-input"
                />
                <div className={`text-right text-xs mt-1 ${(formData.description || '').length >= 2000 ? 'text-red-400' : 'text-gray-500'}`}>
                  {(formData.description || '').length}/2000
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Tipo</label>
                  <select
                    value={formData.nc_type}
                    onChange={(e) => setFormData({...formData, nc_type: e.target.value})}
                    className="input w-full"
                  >
                    {NC_TYPES.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Origem</label>
                  <select
                    value={formData.origin}
                    onChange={(e) => setFormData({...formData, origin: e.target.value})}
                    className="input w-full"
                  >
                    {NC_ORIGINS.map(o => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Severidade</label>
                  <select
                    value={formData.severity}
                    onChange={(e) => setFormData({...formData, severity: e.target.value})}
                    className="input w-full"
                  >
                    {SEVERITIES.map(s => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Responsável</label>
                  <select
                    value={formData.responsible_id}
                    onChange={(e) => setFormData({...formData, responsible_id: e.target.value})}
                    className="input w-full"
                  >
                    <option value="">Selecione...</option>
                    {users.map(u => (
                      <option key={u.id} value={u.id}>{u.full_name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Área/Setor</label>
                  <input
                    type="text"
                    value={formData.area}
                    onChange={(e) => setFormData({...formData, area: e.target.value})}
                    className="input w-full"
                    placeholder="Ex: Produção, Qualidade..."
                    maxLength={100}
                  />
                  <div className={`text-right text-xs mt-1 ${(formData.area || '').length >= 100 ? 'text-red-400' : 'text-gray-500'}`}>
                    {(formData.area || '').length}/100
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Processo</label>
                  <select
                    value={formData.process}
                    onChange={(e) => setFormData({...formData, process: e.target.value})}
                    className="input w-full"
                  >
                    <option value="">Selecione um processo</option>
                    {processes.map(p => (
                      <option key={p.id} value={p.name}>{p.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Data da Ocorrência</label>
                  <input
                    type="date"
                    value={formData.occurrence_date}
                    onChange={(e) => setFormData({...formData, occurrence_date: e.target.value})}
                    className="input w-full"
                    min="1900-01-01"
                    max="2099-12-31"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Data Alvo</label>
                  <input
                    type="date"
                    value={formData.target_date}
                    onChange={(e) => setFormData({...formData, target_date: e.target.value})}
                    className="input w-full"
                    min="1900-01-01"
                    max="2099-12-31"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Evidências</label>
                <textarea
                  value={formData.evidence}
                  onChange={(e) => setFormData({...formData, evidence: e.target.value})}
                  className="input w-full"
                  rows={2}
                  placeholder="Descreva as evidências objetivas"
                  maxLength={2000}
                />
                <div className={`text-right text-xs mt-1 ${(formData.evidence || '').length >= 2000 ? 'text-red-400' : 'text-gray-500'}`}>
                  {(formData.evidence || '').length}/2000
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Ação Imediata</label>
                <textarea
                  value={formData.immediate_action}
                  onChange={(e) => setFormData({...formData, immediate_action: e.target.value})}
                  className="input w-full"
                  rows={2}
                  placeholder="Descreva a ação imediata/correção tomada"
                  maxLength={2000}
                />
                <div className={`text-right text-xs mt-1 ${(formData.immediate_action || '').length >= 2000 ? 'text-red-400' : 'text-gray-500'}`}>
                  {(formData.immediate_action || '').length}/2000
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
                <button type="button" onClick={handleCloseModal} className="btn-secondary">
                  Cancelar
                </button>
                <button type="submit" className="btn-primary" disabled={saving} data-testid="nc-submit-btn">
                  {saving ? <Loader2 className="animate-spin" size={20} /> : editingNc ? 'Salvar' : 'Registrar'}
                </button>
              </div>
            </form>
          </div>
        </>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedNc && (
        <>
          <div className="modal-backdrop" onClick={() => setShowDetailModal(false)} />
          <div className="modal-content p-6 max-w-3xl">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-bold text-white">{selectedNc.code}</h2>
                <p className="text-gray-400">{selectedNc.title}</p>
              </div>
              <button onClick={() => setShowDetailModal(false)} className="text-gray-400 hover:text-white">
                <X size={24} />
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="flex gap-2">
                <StatusBadge status={selectedNc.status} />
                <SeverityBadge severity={selectedNc.severity} />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-gray-400 text-sm">Tipo</p>
                  <p className="text-white">{NC_TYPES.find(t => t.value === selectedNc.nc_type)?.label}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Origem</p>
                  <p className="text-white">{NC_ORIGINS.find(o => o.value === selectedNc.origin)?.label}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Área</p>
                  <p className="text-white">{selectedNc.area || '-'}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Processo</p>
                  <p className="text-white">{selectedNc.process || '-'}</p>
                </div>
              </div>

              <div>
                <p className="text-gray-400 text-sm">Descrição</p>
                <p className="text-white whitespace-pre-wrap">{selectedNc.description}</p>
              </div>

              {selectedNc.evidence && (
                <div>
                  <p className="text-gray-400 text-sm">Evidências</p>
                  <p className="text-white whitespace-pre-wrap">{selectedNc.evidence}</p>
                </div>
              )}

              {selectedNc.immediate_action && (
                <div>
                  <p className="text-gray-400 text-sm">Ação Imediata</p>
                  <p className="text-white whitespace-pre-wrap">{selectedNc.immediate_action}</p>
                </div>
              )}

              {selectedNc.root_cause && (
                <div>
                  <p className="text-gray-400 text-sm">Causa Raiz</p>
                  <p className="text-white whitespace-pre-wrap">{selectedNc.root_cause}</p>
                </div>
              )}

              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-gray-400">Data Ocorrência</p>
                  <p className="text-white">
                    {selectedNc.occurrence_date ? new Date(selectedNc.occurrence_date).toLocaleDateString('pt-BR') : '-'}
                  </p>
                </div>
                <div>
                  <p className="text-gray-400">Data Alvo</p>
                  <p className="text-white">
                    {selectedNc.target_date ? new Date(selectedNc.target_date).toLocaleDateString('pt-BR') : '-'}
                  </p>
                </div>
                <div>
                  <p className="text-gray-400">Data Encerramento</p>
                  <p className="text-white">
                    {selectedNc.closure_date ? new Date(selectedNc.closure_date).toLocaleDateString('pt-BR') : '-'}
                  </p>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
                <button onClick={() => setShowDetailModal(false)} className="btn-secondary">
                  Fechar
                </button>
                <button 
                  onClick={() => { setShowDetailModal(false); handleOpenModal(selectedNc); }} 
                  className="btn-primary"
                >
                  <Edit size={16} />
                  Editar
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default NonConformities;
